<!-- Base.Snackbar.vue (client-lms) -->
<template>
  <Teleport to="body">
    <Transition name="snackbar">
      <div v-if="modelValue" class="lms_snackbar">
        <i
          v-if="displayIcon"
          class="lms_snackbar_icon"
          :class="displayIcon"
          :style="displayIconColor ? { backgroundColor: displayIconColor } : {}"
        ></i>

        <span class="lms_snackbar_message">
          <slot>{{ message }}</slot>
        </span>

        <button v-if="closable" type="button" class="lms_snackbar_close" @click="onClose">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onScopeDispose, ref, watch } from "vue";

const modelValue = defineModel<boolean>({ required: true });

const props = defineProps({
  message: { type: String, default: "" },
  duration: { type: Number, default: 3000 },
  type: { type: String, default: "default" }, // default | warning
  icon: { type: String, default: "" },
  iconColor: { type: String, default: "" },
  closable: { type: Boolean, default: true },
});

const displayIcon = computed(() => {
  if (props.icon) return props.icon;
  if (props.type === "warning") return "icon_triangle_alert";
  return "";
});

const displayIconColor = computed(() => {
  if (props.iconColor) return props.iconColor;
  if (props.type === "warning") return "var(--lms-error-400, #F93C4A)";
  return "";
});

const onClose = () => {
  modelValue.value = false;
};

const timerId = ref<number | null>(null);

const clearTimer = () => {
  if (timerId.value !== null) {
    clearTimeout(timerId.value);
    timerId.value = null;
  }
};

watch(modelValue, (val) => {
  if (val && props.duration > 0) {
    clearTimer();
    timerId.value = window.setTimeout(() => {
      modelValue.value = false;
    }, props.duration);
  } else {
    clearTimer();
  }
});

onScopeDispose(() => {
  clearTimer();
});
</script>

<style scoped>
.lms_snackbar {
  position: fixed;
  bottom: 40px;
  left: 170px;
  width: 568px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 320px;
  padding: 12px 16px;
  background-color: var(--lms-grey-800);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  z-index: 10001;
}

.lms_snackbar_icon {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;
}

.lms_snackbar_message {
  flex: 1;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
  line-height: 24px;
}

.lms_snackbar_close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  background-color: #ffffff;
  cursor: pointer;
  border-radius: 50%;
}

.lms_snackbar_close:hover {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.1);
}

.lms_snackbar_close .mdi {
  display: block;
  font-size: 12px;
  line-height: 1;
  color: var(--lms-grey-800);
}

/* Transition */
.snackbar-enter-active,
.snackbar-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}
.snackbar-enter-from,
.snackbar-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (max-width: 768px) {
  .lms_snackbar {
    left: 16px;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    min-width: 0;
    max-width: none;
    width: auto;
    padding: 12px 14px;
    gap: 10px;
    border-radius: 4px;
  }

  .lms_snackbar_message {
    font-size: 13px;
    line-height: 1.35;
  }

  .lms_snackbar_icon {
    font-size: 16px;
  }

  .lms_snackbar_close {
    width: 22px;
    height: 22px;
  }
}
</style>
